import { useEffect, useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import axios from 'axios';
import {
  Button,
  Box,
  IconButton,
  Dialog,
  Snackbar,
  DialogActions,
  DialogContentText,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
  Paper,
  Tooltip,
  Avatar
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import CreateUser from './createUser';
// import { CiExport } from 'react-icons/ci';
// import { CSVLink } from 'react-csv';
import { useTheme } from '@emotion/react';

const token = async () => {
  return localStorage.getItem('token');
};

// API function to get all users
const getUsers = async () => {
  try {
    const res = await axios.post('https://db.neophyte.live/sentinel-api/api/sentinel/getAllUsers', {
      headers: {
        Accept: 'application/json',
        Authorization: await token()
      }
    });
    return res;
  } catch (error) {
    console.log('Error while calling team API:', error);
    return { data: { users: [] } };
  }
};

// Function to get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const names = name.split(' ');
  if (names.length >= 2) {
    return `${names[0][0]}${names[1][0]}`.toUpperCase();
  }
  return name.charAt(0).toUpperCase();
};

// Function to generate a consistent color based on name
const generateColorFromName = (name) => {
  if (!name) return '#44BE54';

  const colors = [
    '#1976d2',
    '#388e3c',
    '#d32f2f',
    '#7b1fa2',
    '#0288d1',
    '#fbc02d',
    '#e64a19',
    '#5d4037',
    '#455a64',
    '#00796b',
    '#c2185b',
    '#512da8'
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

const TableData = () => {
  const [data, setDataApi] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [checked, setChecked] = useState(true);
  // const [dataexport, setDataexport] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deletionInProgress, setDeletionInProgress] = useState(false);
  const [deletionSuccess, setDeletionSuccess] = useState(null);
  const [apiError, setApiError] = useState(null);
  const theme = useTheme();

  // Mock user data from localStorage
  const userData = {
    _id: 'admin1',
    user_role: 'nhq',
    user_name: 'Admin User'
  };

  const userRole = userData ? userData.user_role : null;
  const user_id = userData ? userData._id : null;

  useEffect(() => {
    // Export data function
    async function exportData() {
      setLoading(true);
      try {
        const response = await getUsers();
        const allUsers = response?.data?.users || [];

        // Filter users based on disabled status (equivalent to Active/Inactive)
        const filteredData = allUsers.filter((user) => (checked ? !user.disabled : user.disabled));

        setDataexport(filteredData);
      } catch (error) {
        console.error('Error fetching export data:', error);
        // setApiError('Failed to fetch export data');
      } finally {
        setLoading(false);
      }
    }

    exportData();
  }, [checked]);

  const getAllUser = async () => {
    setLoading(true);
    try {
      const response = await getUsers();
      const allUsers = response?.data?.users || [];

      console.log('API Response:', allUsers); // For debugging

      // Filter users based on disabled status (equivalent to Active/Inactive)
      const filteredData = allUsers.filter((user) => (checked ? !user.disabled : user.disabled));

      // Collect all usernames for validation in the create user form
      const usernames = allUsers.map((user) => user.username).filter(Boolean);
      setExistingUsernames(usernames);

      // Format all data for the table according to the new API response structure
      const formattedData = filteredData.map((item) => {
        return {
          id: item._id || item.id,
          img: item.image || null,
          name: item.full_name || 'Unknown User',
          username: item.username || '',
          email: item.email || '',
          number: item.number || '',
          role: item.role || 'User',
          // Store names are now directly available in the API response
          stores: item.stores || [],
          status: item.disabled ? 'Inactive' : 'Active'
        };
      });

      setDataApi(formattedData);
      setTotalRows(filteredData.length);
      setApiError(null);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setApiError('Failed to fetch user data');
      setDataApi([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the create user modal
  const handleOpenCreateUserModal = () => {
    setShowCreateUserModal(true);
  };

  // Handle closing the create user modal
  const handleCloseCreateUserModal = () => {
    setShowCreateUserModal(false);
  };

  // Handle successful user creation
  const handleUserCreationSuccess = () => {
    // Refresh the user list
    getAllUser();
    // Show success message
    setDeletionSuccess(true); // Reusing the existing success notification
    // Close the modal
    handleCloseCreateUserModal();
  };

  // Handle successful user update
  const handleUserUpdateSuccess = () => {
    // Refresh the user list
    getAllUser();
    // Show success message
    setDeletionSuccess(true); // Reusing the existing success notification
    // Close the modal
    handleEditUserDialogClose();
  };

  useEffect(() => {
    getAllUser();
    // eslint-disable-next-line
  }, [pagination.pageIndex, pagination.pageSize, checked]);

  // No need for store mapping as store names are directly available in the API response

  // State for store details modal
  const [showStoresModal, setShowStoresModal] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [storeSearchQuery, setStoreSearchQuery] = useState('');
  const [filteredStores, setFilteredStores] = useState([]);

  // Create user modal state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [existingUsernames, setExistingUsernames] = useState([]);

  // Function to get store names (now directly available in API response)
  const getStoreNames = (stores) => {
    if (!stores || !Array.isArray(stores) || stores.length === 0) return 'N/A';

    // Ensure we're returning an array of store names
    return stores.map((store) => String(store)); // Convert all items to strings to ensure consistent handling
  };

  // Function to handle opening the stores modal
  const handleOpenStoresModal = (stores, userName) => {
    setSelectedStores(stores);
    setFilteredStores(stores);
    setSelectedUserName(userName);
    setStoreSearchQuery('');
    setShowStoresModal(true);
  };

  // Function to handle closing the stores modal
  const handleCloseStoresModal = () => {
    setShowStoresModal(false);
  };

  // Function to handle store search
  const handleStoreSearch = (event) => {
    const query = event.target.value;
    setStoreSearchQuery(query);

    if (!query.trim()) {
      setFilteredStores(selectedStores);
    } else {
      const filtered = selectedStores.filter((store) => store.toLowerCase().includes(query.toLowerCase()));
      setFilteredStores(filtered);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ cell }) => {
          const user = cell.row.original;
          const userName = user.name || 'Unknown User';
          const bgColor = generateColorFromName(userName);
          const initials = getInitials(userName);

          return (
            <div className="product flex items-center gap-3 py-2">
              {user.img ? (
                <Avatar
                  src={user.img}
                  alt={userName}
                  sx={{
                    width: 40,
                    height: 40,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '2px solid #fff'
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: bgColor,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '2px solid #fff'
                  }}
                >
                  {initials}
                </Avatar>
              )}
              <span className="productinn flex flex-col">
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    color: 'rgba(0,0,0,0.87)',
                    fontSize: '14px',
                    lineHeight: 1.2
                  }}
                >
                  {userName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(0,0,0,0.6)',
                    textTransform: 'capitalize',
                    fontWeight: 400,
                    fontSize: '12px'
                  }}
                >
                  {user.role}
                </Typography>
              </span>
            </div>
          );
        },
        size: 200,
        order: 1,
        enableSorting: true
      },
      {
        accessorKey: 'username',
        header: 'User Name',
        enableSorting: true,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0,0,0,0.87)',
              fontWeight: 400,
              fontSize: '13px'
            }}
          >
            {cell.row.original.username || 'N/A'}
          </Typography>
        ),
        size: 150,
        order: 2
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: false,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0,0,0,0.87)',
              fontWeight: 400,
              fontSize: '13px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '200px'
            }}
          >
            {cell.row.original.email || 'N/A'}
          </Typography>
        ),
        size: 200,
        order: 3
      },
      {
        accessorKey: 'number',
        header: 'Number',
        enableSorting: false,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0,0,0,0.87)',
              fontWeight: 400,
              fontSize: '13px'
            }}
          >
            {cell.row.original.number || 'N/A'}
          </Typography>
        ),
        size: 150,
        order: 4
      },
      {
        accessorKey: 'stores',
        header: 'Stores',
        enableSorting: false,
        Cell: ({ cell }) => {
          const storeNames = getStoreNames(cell.row.original.stores);
          const userName = cell.row.original.name;

          // Debug log to check what's coming from the API
          console.log('Stores for', userName, ':', cell.row.original.stores);
          console.log('Processed storeNames:', storeNames);

          if (storeNames === 'N/A') {
            return <div style={{ color: 'rgba(0,0,0,0.5)' }}>N/A</div>;
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                className="bg-gray-100 text-gray-700 font-medium px-3 py-1 rounded-lg"
                style={{
                  backgroundColor: 'rgba(0,0,0,0.06)',
                  color: 'rgba(0,0,0,0.75)',
                  fontWeight: 500,
                  fontSize: '12px',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  display: 'inline-block',
                  maxWidth: '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {storeNames.length > 0 ? storeNames[0] : 'N/A'}
                {storeNames.length > 1 ? ` + ${storeNames.length - 1} more` : ''}
              </div>
              {storeNames.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => handleOpenStoresModal(storeNames, userName)}
                  sx={{ padding: '2px', color: '#1976d2' }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </IconButton>
              )}
            </div>
          );
        },
        size: 200,
        order: 5
      }
    ],
    []
  );

  function canModify(currentUserRole, targetUserRole, targetUserId) {
    const currRole = currentUserRole?.toLowerCase();
    if (currRole === 'nhq') return true;
    if (targetUserRole === currentUserRole && user_id === targetUserId) return true;
    const accessMap = {
      nhq: ['cluster manager', 'store manager', 'agent'],
      'cluster manager': ['store manager', 'agent'],
      'store manager': ['agent'],
      agent: []
    };

    currentUserRole = currentUserRole?.toLowerCase();
    targetUserRole = targetUserRole?.toLowerCase();

    if (!accessMap[currentUserRole]) {
      return false;
    }

    return accessMap[currentUserRole].includes(targetUserRole);
  }

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  // const prepareExportData = () => {
  //   const dataForExport = dataexport.map((row) => ({
  //     Name: row.full_name,
  //     Username: row.username,
  //     Email: row.email,
  //     Phone: row.mobile_number,
  //     Status: row.disabled ? 'Inactive' : 'Active',
  //     Creation_Date: new Date(row.createdAt).toLocaleDateString()
  //   }));
  //   return dataForExport;
  // };

  const [editRowId, setEditRowId] = useState(null);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editUserData, setEditUserData] = useState(null);

  const handleEditUserDialogOpen = (rowId) => {
    // Find the user data from the table data
    const userData = data.find((user) => user.id === rowId);
    if (userData) {
      setEditRowId(rowId);
      setEditUserData(userData);
      setShowEditUserDialog(true);
    }
  };

  const handleEditUserDialogClose = () => {
    setEditRowId(null);
    setEditUserData(null);
    setShowEditUserDialog(false);
  };

  const handleConfirmDialogOpen = (userId) => {
    setDeleteUserId(userId);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setDeleteUserId(null);
    setConfirmDialogOpen(false);
  };

  const handleUserDeletion = async () => {
    setDeletionInProgress(true);
    try {
      await axios.post(`https://db.neophyte.live/sentinel-api/api/sentinel/delete/${deleteUserId}`, {
        headers: {
          Authorization: await token()
        }
      });

      // Simulate deletion for now
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setDeletionSuccess(true);
      // Refresh data
      getAllUser();
    } catch (error) {
      console.error('Error deleting user:', error);
      setDeletionSuccess(false);
    } finally {
      setDeletionInProgress(false);
      handleConfirmDialogClose();
    }
  };

  const handleDeleteSelectedRows = async () => {
    setDeletionInProgress(true);
    try {
      // Replace with your actual bulk delete API call
      // Example:
      // await axios.post(https://db.neophyte.live/sentinel-api/api/sentinel/bulkDeleteUsers, {
      //   userIds: selectedRowIds
      // }, {
      //   headers: {
      //     Authorization: await token()
      //   }
      // });

      // Simulate deletion for now
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setDeletionSuccess(true);
      // Refresh data
      getAllUser();
    } catch (error) {
      console.error('Error deleting selected users:', error);
      setDeletionSuccess(false);
    } finally {
      setDeletionInProgress(false);
    }
  };

  const table = useMaterialReactTable({
    columns,
    data,
    enableMultiRowSelection: true,
    enableRowSelection: true,
    enableSelectAll: true,
    enableSubRowSelection: true,
    enableColumnOrdering: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableFilters: true,
    initialState: { density: 'compact', pageSize: 10, pageIndex: 0 },
    onPaginationChange: setPagination,
    manualPagination: false,
    rowCount: totalRows,
    manualFiltering: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: { globalFilter, pagination, isLoading: loading, sorting },

    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '5px' }}>
        <Tooltip title="Edit User">
          <IconButton
            onClick={() => {
              canModify(userRole, row.original.role, row.original.id) && handleEditUserDialogOpen(row.original.id);
            }}
            color="primary"
            aria-label="edit"
            sx={{
              fontSize: '14px',
              lineHeight: '17px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              },
              '&:focus': {
                outline: 'none'
              }
            }}
            disabled={!canModify(userRole, row.original.role, row.original.id)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>

        {/* Edit User Dialog - Using CreateUser component in edit mode */}
        <Dialog
          key={row.original.id}
          open={editRowId === row.original.id && showEditUserDialog}
          onClose={handleEditUserDialogClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
            }
          }}
          maxWidth="md"
          fullWidth
        >
          {/* We're using the CreateUser component in edit mode */}
          {editUserData && (
            <CreateUser
              open={showEditUserDialog}
              onClose={handleEditUserDialogClose}
              onSuccess={handleUserUpdateSuccess}
              existingUsernames={existingUsernames}
              editMode={true}
              userData={editUserData}
            />
          )}
        </Dialog>

        <Tooltip title="Delete User">
          <IconButton
            color="error"
            onClick={() => {
              canModify(userRole, row.original.role, row.original.id) && handleConfirmDialogOpen(row.original.id);
            }}
            disabled={!canModify(userRole, row.original.role, row.original.id)}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)'
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),

    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: '1rem',
          p: '4px',
          alignItems: 'center'
        }}
      >
        {/* Create User Button */}
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateUserModal}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: '0 4px 6px rgba(25, 118, 210, 0.1)',
            '&:hover': {
              boxShadow: '0 6px 10px rgba(25, 118, 210, 0.2)'
            }
          }}
        >
          Create User
        </Button>

        {/* Export to CSV feature is commented out for now */}
        {/* 
        <Tooltip title="Export to CSV">
          <CSVLink
            data={prepareExportData()}
            filename={'users.csv'}
            target="_blank"
            className="btn btn-primary"
            style={{ textDecoration: 'none', cursor: 'pointer', fontSize: '30px', marginTop: '8px' }}
          >
            <CiExport className="text-[#212b36]" />
          </CSVLink>
        </Tooltip>
        */}

        <FormControlLabel
          control={
            <Switch
              checked={checked}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
              name="Active"
              color="success"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#44BE54'
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#44BE54'
                }
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: checked ? '#44BE54' : 'rgba(0,0,0,0.6)'
              }}
            >
              {checked ? 'Active Users' : 'Inactive Users'}
            </Typography>
          }
        />

        <Tooltip title="Refresh Data">
          <IconButton
            onClick={() => getAllUser()}
            color="primary"
            sx={{
              borderRadius: '8px',
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)'
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        {table.getSelectedRowModel().rows.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              const selectedRows = table.getSelectedRowModel().rows;
              const selectedRowData = selectedRows.map((item) => item.original.id);
              handleDeleteSelectedRows(selectedRowData);
            }}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 4px 6px rgba(211, 47, 47, 0.1)',
              '&:hover': {
                boxShadow: '0 6px 10px rgba(211, 47, 47, 0.2)'
              }
            }}
          >
            Delete Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        )}
      </Box>
    ),

    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
        fontSize: '13px',
        color: 'white',
        backgroundColor: '#44BE54',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '12px 16px',
        '&:first-of-type': {
          borderTopLeftRadius: '8px'
        },
        '&:last-child': {
          borderTopRightRadius: '8px'
        }
      }
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        background: '#ffff',
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.08)',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #f0f0f0'
      }
    },
    muiTableContainerProps: {
      sx: {
        backgroundColor: '#fff'
      }
    },
    muiTableBodyCellProps: {
      sx: {
        background: '#ffff',
        padding: '8px 16px',
        fontSize: '14px',
        borderBottom: '1px solid #f5f5f5'
      }
    },
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02) !important'
        }
      }
    },
    muiTopToolbarProps: {
      sx: {
        background: '#ffff',
        borderBottom: '1px solid #f0f0f0',
        padding: '16px'
      }
    },
    muiBottomToolbarProps: {
      sx: {
        background: '#ffff',
        borderTop: '1px solid #f0f0f0'
      }
    },
    muiSkeletonProps: {
      animation: 'wave'
    },
    muiLinearProgressProps: {
      color: 'success'
    },
    muiCircularProgressProps: {
      color: 'success'
    },
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      variant: 'outlined',
      size: 'medium'
    }
  });

  return (
    <div style={{ display: 'flex' }} className="-mt-12">
      <div style={{ flex: 1, padding: '20px' }} className=" min-h-screen">
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid #f0f0f0'
          }}
        >
          {apiError && (
            <Alert
              severity="error"
              sx={{
                margin: '16px',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(244, 67, 54, 0.1)'
              }}
            >
              {apiError}
            </Alert>
          )}

          <MaterialReactTable table={table} />
        </Paper>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: '12px',
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
            padding: '20px'
          }
        }}
      >
        {deletionInProgress ? (
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress color="error" />
          </DialogContent>
        ) : (
          <>
            <DialogTitle
              id="alert-dialog-title"
              style={{
                paddingBottom: '10px',
                color: '#333',
                fontSize: '24px',
                fontWeight: 600
              }}
            >
              Confirm Deletion
            </DialogTitle>

            <DialogContent style={{ color: '#555', fontSize: '18px' }}>
              <DialogContentText id="alert-dialog-description">Are you sure you want to delete this user?</DialogContentText>
            </DialogContent>
            <DialogActions style={{ padding: '10px 20px' }}>
              <Button
                onClick={handleUserDeletion}
                color="primary"
                variant="contained"
                sx={{
                  bgcolor: theme.palette.success.main,
                  borderRadius: '8px',
                  padding: '6px 24px',
                  transition: 'background-color 0.3s ease',
                  '&:active': {
                    backgroundColor: theme.palette.success.light
                  },
                  '&:focus': {
                    outline: 'none'
                  },
                  mr: 1
                }}
              >
                Confirm
              </Button>

              <Button
                onClick={handleConfirmDialogClose}
                color="primary"
                variant="outlined"
                autoFocus
                sx={{
                  borderColor: theme.palette.error.main,
                  bgcolor: theme.palette.error.light,
                  color: theme.palette.error.main,
                  borderRadius: '8px',
                  padding: '6px 24px',
                  '&:hover': {
                    backgroundColor: theme.palette.error.light,
                    color: theme.palette.text.secondary
                  }
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={deletionSuccess !== null}
        autoHideDuration={6000}
        onClose={() => setDeletionSuccess(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <Alert onClose={() => setDeletionSuccess(null)} severity={deletionSuccess ? 'success' : 'error'}>
          {deletionSuccess ? 'Operation completed successfully!' : 'Operation failed.'}
        </Alert>
      </Snackbar>

      {/* Stores Modal */}
      <Dialog
        open={showStoresModal}
        onClose={handleCloseStoresModal}
        maxWidth="md"
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '0',
            minWidth: '500px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            pb: 2,
            pt: 2,
            px: 3,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          Stores assigned to {selectedUserName}
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {/* Search Bar */}
          <Box sx={{ mb: 3, position: 'relative' }}>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                padding: '8px 12px'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: 'rgba(0, 0, 0, 0.54)', marginRight: '8px' }}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder="Search stores..."
                value={storeSearchQuery}
                onChange={handleStoreSearch}
                style={{
                  border: 'none',
                  outline: 'none',
                  width: '100%',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
              {storeSearchQuery && (
                <button
                  onClick={() => {
                    setStoreSearchQuery('');
                    setFilteredStores(selectedStores);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: 'rgba(0, 0, 0, 0.54)' }}
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </Box>

          {/* Stores Display */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {filteredStores.length > 0 ? (
              filteredStores.map((store, index) => (
                <Chip
                  key={index}
                  label={store}
                  sx={{
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: '#1976d2',
                    fontWeight: 500,
                    borderRadius: '16px',
                    padding: '4px',
                    margin: '4px'
                  }}
                />
              ))
            ) : storeSearchQuery ? (
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', py: 2 }}>
                No stores found matching &quot;{storeSearchQuery}&quot;
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', py: 2 }}>
                No stores assigned
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button onClick={handleCloseStoresModal} color="primary" variant="contained" sx={{ borderRadius: '8px' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Modal */}
      <CreateUser
        open={showCreateUserModal}
        onClose={handleCloseCreateUserModal}
        onSuccess={handleUserCreationSuccess}
        existingUsernames={existingUsernames}
      />
    </div>
  );
};

export default TableData;
